import React, { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import PostImageCropModal from '../../../components/PostImageCropModal';
import { getImageUrl } from '../../../utils/imageUtils';
import { uploadMyPostImage } from '../../../api/posts';

/**
 * Simple modal for creating a post (Full-only, owner-only UI gating is done by parent).
 * Uses the `useCreatePost()` mutation passed in from parent.
 */
const PostCreateModal = ({
    isOpen,
    onClose,
    createPost,
    title,
    setTitle,
    body,
    setBody,
    imageUrl,
    setImageUrl,
    linkUrl,
    setLinkUrl,
    visibility,
    setVisibility,
    onCreated
}) => {
    const [imgModalOpen, setImgModalOpen] = useState(false);
    const [imgSrc, setImgSrc] = useState(null);
    const [imgUploading, setImgUploading] = useState(false);

    // IMPORTANT: Hooks must not be called conditionally.
    // We render nothing when closed, but still keep hook order stable.
    if (!isOpen) return null;

    const handleFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setImgSrc(ev.target.result);
            setImgModalOpen(true);
        };
        reader.onerror = () => toast.error('Не удалось прочитать изображение');
        reader.readAsDataURL(file);
    };

    const handleUpload = async (croppedFile) => {
        setImgUploading(true);
        try {
            const url = await uploadMyPostImage(croppedFile);
            setImageUrl(url);
            toast.success('Изображение загружено');
            setImgModalOpen(false);
            setImgSrc(null);
        } catch (e) {
            toast.error(e?.message || 'Ошибка при загрузке изображения');
        } finally {
            setImgUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white border border-secondary-200 shadow-soft">
                <div className="px-5 py-4 border-b border-secondary-200 flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="text-sm text-secondary-500">Пост</div>
                        <div className="text-lg font-semibold text-secondary-900 truncate">Создать пост</div>
                    </div>
                    <button type="button" className="p-2 rounded-lg hover:bg-secondary-50" onClick={onClose} title="Закрыть">
                        <X className="w-5 h-5 text-secondary-600" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Заголовок</label>
                        <input
                            className="input w-full"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Название поста…"
                            maxLength={200}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Текст</label>
                        <textarea
                            className="input w-full h-28 resize-none"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Напишите пост…"
                            maxLength={5000}
                        />
                        <div className="mt-1 text-xs text-secondary-500">{(body || '').length} / 5000</div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1 flex flex-wrap items-center gap-2">
                            Изображение (опционально)
                            <button
                                type="button"
                                className="btn btn-secondary btn-xs"
                                onClick={() => document.getElementById('post-image-input').click()}
                                disabled={imgUploading}
                            >
                                {imageUrl ? 'Заменить' : 'Загрузить'}
                            </button>
                            <input
                                id="post-image-input"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleFile(file);
                                    e.target.value = '';
                                }}
                            />
                            {imageUrl && (
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs text-red-600"
                                    onClick={() => setImageUrl('')}
                                    disabled={imgUploading}
                                >
                                    Удалить
                                </button>
                            )}
                        </label>
                        {imageUrl && (
                            <div className="mt-2">
                                <img
                                    src={getImageUrl(imageUrl)}
                                    alt="Post"
                                    className="w-24 h-24 object-cover rounded border"
                                />
                            </div>
                        )}
                        {imgUploading && (
                            <div className="mt-2 text-xs text-secondary-600">Загрузка изображения…</div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Ссылка (опционально)</label>
                        <input
                            className="input w-full"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            placeholder="https://…"
                            maxLength={2000}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Статус</label>
                            <div className="text-xs text-secondary-500 mb-1">
                                Публикации отправляются на модерацию. Администратор подтвердит и опубликует.
                            </div>
                            <select className="input w-full" value="draft" disabled>
                                <option value="draft">Ожидает модерации</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Доступ</label>
                            <select className="input w-full" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                                <option value="public">Публичный</option>
                                <option value="unlisted">По ссылке</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="px-5 py-4 border-t border-secondary-200 flex items-center justify-end gap-2">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Отмена
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={createPost.isLoading || imgUploading || !String(title || '').trim() || !String(body || '').trim()}
                        onClick={async () => {
                            try {
                                await createPost.mutateAsync({
                                    title: String(title || '').trim(),
                                    body: String(body || '').trim(),
                                    image_url: String(imageUrl || '').trim() || null,
                                    link_url: String(linkUrl || '').trim() || null,
                                    status: 'draft',
                                    visibility
                                });
                                toast.success('Пост создан');
                                onCreated?.();
                            } catch (e) {
                                const msg = e?.response?.data?.error?.message || e?.response?.data?.error || 'Ошибка создания поста';
                                toast.error(msg);
                            }
                        }}
                    >
                        {createPost.isLoading ? 'Сохранение…' : 'Создать'}
                    </button>
                </div>
            </div>

            <PostImageCropModal
                isOpen={imgModalOpen}
                imageSrc={imgSrc}
                onClose={() => {
                    if (imgUploading) return;
                    setImgModalOpen(false);
                    setImgSrc(null);
                }}
                onConfirm={handleUpload}
            />
        </div>
    );
};

export default PostCreateModal;
